import groupBy from 'lodash.groupby';
import { Vue } from 'vue-property-decorator';
import { EventModules } from './EventModules';
import { EventRunPresenceChannel } from './EventRunPresenceChannel';
import { RESEARCH_ELECTRONIC_SIGNATURES_ERRORS, RESEARCH_PIPELINE_ERRORS } from '@/config/research';

export const PipelineEvents = {
  namespaced: true,

  modules: {
    modules: EventModules,
    presence: EventRunPresenceChannel,
  },

  state: {
    eventRun: null,
    events: [],
    canByIdAndVersion: {},
    stagesByPipeline: {},
    runDetails: null,
    loading: false,
    currentEventLoading: false,
    loadingRuns: false,
    saving: false,
    currentEvent: {},
    stagesLoadedByPipeline: {},
    triggerOpenAddStageModal: null,
    segmentationSaveStarted: false,
    stageOnEdition: {},
    eSignatureError: null,
    isSignSuccessful: false,
    isManualLockInProgress: false,
    isManualUnlockInProgress: false,
    isManualLockSuccessful: false,
    isManualUnlockSuccessful: false,
    isManualUnlockFail: false,
    isManualLockFail: false,
    currentEventId: null,
    pipelineEventError: null,
  },

  getters: {
    getPipelineEvents: ({ events }) => events,
    getCanEditEvent:
      ({ canByIdAndVersion }) =>
      (id, version) =>
        !!canByIdAndVersion[`${id} ${version}`]?.edit,
    getPipelineStages:
      ({ stagesByPipeline }) =>
      (pipelineUuid) =>
        stagesByPipeline[pipelineUuid] ?? [],
    areEventsLoading: ({ loading }) => loading,
    isCurrentEventLoading: ({ currentEventLoading }) => currentEventLoading,
    areRunsLoading: ({ loadingRuns }) => loadingRuns,
    areEventsSaving: ({ saving }) => saving,
    isEventRun: ({ eventRun }) => eventRun,
    areStagesLoaded:
      ({ stagesLoadedByPipeline }) =>
      (pipelineUuid) =>
        !!stagesLoadedByPipeline[pipelineUuid],

    getEventRunDetails: ({ runDetails }) => runDetails,

    getEventsIndexedByStage: ({ events }) => {
      return groupBy(events, (event) => event.stage?.uuid);
    },

    getEventsForStage: (_state, getters) => (stageUuid) => {
      const index = getters.getEventsIndexedByStage;

      return index[stageUuid] ?? [];
    },

    getStageOnEdition: ({ stageOnEdition }) => stageOnEdition,
    getCurrentEvent: ({ currentEvent }) => currentEvent,
    triggerOpenAddStageModal: ({ triggerOpenAddStageModal }) => triggerOpenAddStageModal,
    segmentationSaveStarted: ({ segmentationSaveStarted }) => segmentationSaveStarted,
  },

  mutations: {
    setPipelineEvents(state, events) {
      state.events = [...events];
    },

    setPipelineStages(state, { pipeline, stages }) {
      Vue.set(state.stagesByPipeline, pipeline, stages);
    },

    deleteStage(state, { pipeline, stage }) {
      Vue.delete(
        state.stagesByPipeline[pipeline],
        state.stagesByPipeline[pipeline].findIndex(({ uuid }) => uuid === stage)
      );
    },

    addEvent(state, event) {
      state.events = [...state.events, event];
    },

    updateEvent(state, newEventData) {
      state.events = [
        ...state.events.map((ev) => {
          if (ev.uuid === newEventData.uuid) {
            return newEventData;
          }

          return ev;
        }),
      ];
    },

    updateStage(state, { pipeline, newStage }) {
      state.stagesByPipeline[pipeline] = [
        ...state.stagesByPipeline[pipeline].map((stage) => {
          if (stage.uuid === newStage.uuid) {
            return newStage;
          }

          return stage;
        }),
      ];
    },

    deleteEvent(state, eventUuid) {
      state.events = [...state.events.filter(({ uuid }) => uuid !== eventUuid)];
    },

    addStage(state, { pipeline, newStage }) {
      state.stagesByPipeline[pipeline] = [...(state.stagesByPipeline[pipeline] ?? []), newStage];
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setCurrentEventLoading(state, currentEventLoading) {
      state.currentEventLoading = currentEventLoading;
    },

    setRunsLoading(state, loading) {
      state.loadingRuns = loading;
    },

    setSaving(state, saving) {
      state.saving = saving;
    },

    setCurrentEvent(state, eventData) {
      state.currentEvent = eventData;
    },

    setRunDetails(state, runDetails) {
      state.runDetails = runDetails;
    },

    setEventRun(state, eventRun) {
      state.eventRun = eventRun;
    },

    setCan(state, { id, version, value }) {
      Vue.set(state.canByIdAndVersion, [`${id} ${version}`], value);
    },

    setStagesLoadedByPipeline(state, { pipeline, value }) {
      state.stagesLoadedByPipeline = {
        ...state.stagesLoadedByPipeline,
        [pipeline]: value,
      };
    },

    triggerOpenAddStage(state) {
      state.triggerOpenAddStageModal = new Date();
    },

    unloadEvent(state) {
      state.currentEvent = {};
    },

    setSegmentationSaveStarted(state, value) {
      state.segmentationSaveStarted = value;
    },

    setStageOnEdition(state, stage) {
      state.stageOnEdition = stage;
    },

    setESignatureError(state, error) {
      state.eSignatureError = error;
    },

    setIsSignSuccessful(state, flag) {
      state.isSignSuccessful = flag;
    },
    setIsManualLockInProgress(state, flag) {
      state.isManualLockInProgress = flag;
    },

    setIsManualUnLockInProgress(state, flag) {
      state.isManualUnlockInProgress = flag;
    },

    setIsManualUnlockSuccessful(state, flag) {
      state.isManualUnlockSuccessful = flag;
    },
    setIsManualUnlockFail(state, flag) {
      state.isManualUnlockFail = flag;
    },

    setIsManualLockSuccessful(state, flag) {
      state.isManualLockSuccessful = flag;
    },
    setIsManualLockFail(state, flag) {
      state.isManualLockFail = flag;
    },

    setCurrentEventId(state, payload) {
      state.currentEventId = payload;
    },

    setPipelineEventError(state, payload) {
      state.pipelineEventError = payload;
    },
  },

  actions: {
    async loadEvents({ commit, dispatch }, { repository, project, pipeline, version }) {
      if (!repository || !project || !pipeline) {
        return;
      }

      version = version ?? null;

      let events = [];
      let can = null;
      commit('setLoading', true);
      commit('setPipelineEvents', events);
      dispatch('loadStages', { repository, project, pipeline, version });
      // NOTE: This call is not correct. There is no intrinsic link between both requests

      try {
        const {
          data: { data },
        } = await this._vm.$api('repositories.projects.pipelines.events.index', {
          version: '1.0',
          project,
          repository,
          pipeline,
          v: version,
        });

        events = data;
      } catch (err) {
        console.error(err);
      }

      commit('setPipelineEvents', events);
      commit('setLoading', false);
      commit('setCan', { id: pipeline, version, value: can });
    },

    onEditStageConfigAction({ commit }, stage) {
      commit('setStageOnEdition', stage ? stage : {});
    },

    async loadEvent({ commit }, { repository, project, pipeline, event, version }) {
      if (!repository || !project || !event || !pipeline) {
        return;
      }

      let eventResponse = null;

      commit('setCurrentEventLoading', true);

      try {
        const evtResponse = await this._vm.$api('repositories.projects.pipelines.events.show', {
          version: '1.0',
          project,
          repository,
          pipeline,
          event,
          v: version,
        });

        eventResponse = evtResponse.data.data;
        const eventPipeline = evtResponse.data.pipeline ?? null;

        if (eventPipeline) {
          eventResponse = { ...eventResponse, pipeline: eventPipeline };
        }
      } catch (err) {
        console.error(err);
      }

      commit('setCurrentEvent', eventResponse);
      commit('setCurrentEventLoading', false);
    },

    async loadStages({ commit }, { repository, project, pipeline, version }) {
      if (!repository || !project || !pipeline) {
        return;
      }

      commit('setStagesLoadedByPipeline', { pipeline, value: false });

      let stages = [];

      commit('setLoading', true);
      try {
        const response = await this._vm.$api('repositories.projects.pipelines.stages.index', {
          version: '1.0',
          project,
          repository,
          pipeline,
          pipelineVersion: version,
        });

        stages = response.data.data;
      } catch (err) {
        console.error(err);
      }

      commit('setPipelineStages', { pipeline, stages });
      commit('setLoading', false);
      commit('setStagesLoadedByPipeline', { pipeline, value: true });
    },

    async createEvent({ commit }, { repository, project, pipeline, data, blindMetadata }) {
      commit('setSaving', true);
      let newEvent = null;

      try {
        const response = await this._vm.$api(
          'repositories.projects.pipelines.events.store',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
            blindMetadata,
          },
          { ...data }
        );

        newEvent = response.data.data;
      } catch (err) {
        console.error(err);
      }

      if (newEvent) {
        commit('addEvent', newEvent);
      }

      commit('setSaving', false);
    },

    async updateEvent({ commit, dispatch }, { repository, project, pipeline, event, data, blindMetadata }) {
      commit('setSaving', true);
      commit('setPipelineEventError', null);
      let newEvent = null;

      try {
        const response = await this._vm.$api(
          'repositories.projects.pipelines.events.update',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
            event,
            blindMetadata,
          },
          { ...data }
        );

        newEvent = response.data.data;
      } catch (err) {
        if (err?.message?.includes(RESEARCH_PIPELINE_ERRORS.EVENT_QC_IN_USE)) {
          commit('setPipelineEventError', RESEARCH_PIPELINE_ERRORS.EVENT_QC_IN_USE);
          commit('setSaving', false);
          return;
        }
        console.error(err);
      }

      if (newEvent) {
        commit('updateEvent', newEvent);
      }

      await dispatch('loadEvents', { repository, project, pipeline });
      commit('setSaving', false);
    },

    async deleteEvent({ commit }, { repository, project, pipeline, event, comment }) {
      commit('setSaving', true);

      try {
        await this._vm.$api(
          'repositories.projects.pipelines.events.destroy',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
            event,
          },
          { comment }
        );

        commit('deleteEvent', event);
      } catch (err) {
        console.error(err);
      }

      commit('setSaving', false);
    },

    async createStage({ commit }, { repository, project, pipeline, name }) {
      commit('setSaving', true);
      let newStage = null;

      try {
        const response = await this._vm.$api(
          'repositories.projects.pipelines.stages.store',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
          },
          { name }
        );

        newStage = response.data.data;
      } catch (err) {
        console.error(err);
      }

      if (newStage) {
        commit('addStage', { pipeline, newStage });
      }

      commit('setSaving', false);
    },

    async updateStage({ commit }, { repository, project, pipeline, stage, name }) {
      commit('setSaving', true);
      let newStage = null;

      try {
        const response = await this._vm.$api(
          'repositories.projects.pipelines.stages.update',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
            stage,
          },
          { name }
        );

        newStage = response.data.data;
      } catch (err) {
        console.error(err);
      }

      if (newStage) {
        commit('updateStage', { pipeline, newStage });
      }

      commit('setSaving', false);
    },

    async deleteStage({ commit }, { repository, project, pipeline, stage }) {
      commit('deleteStage', { pipeline, stage });

      try {
        await this._vm.$api('repositories.projects.pipelines.stages.destroy', {
          version: '1.0',
          repository,
          project,
          pipeline,
          stage,
        });
      } catch (err) {
        console.error(err);
      }
    },

    async runEvent({ commit }, { repository, project, pipeline, event, subject, version, data }) {
      commit('setSaving', true);
      let eventRun = null;

      try {
        const responseRun = await this._vm.$api(
          'repositories.projects.pipelines.events.run',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
            event,
            subject,
            v: version,
          },
          { modules: data }
        );
        eventRun = responseRun.data.data;
      } catch (err) {
        console.error(err);
      }

      commit('setEventRun', eventRun);
      commit('setSaving', false);
    },

    async approveEventRun({ commit }, { repository, project, pipeline, event, subject, version, data }) {
      commit('setSaving', true);
      commit('setIsSignSuccessful', false);
      commit('setESignatureError', null);

      try {
        await this._vm.$api(
          'repositories.projects.pipelines.events.run.e-sign',
          {
            version: '1.0',
            repository,
            project,
            pipeline,
            event,
            subject,
            v: version,
          },
          { ...data }
        );

        commit('setIsSignSuccessful', true);
      } catch (error) {
        console.error(error);

        if (error.message === RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE) {
          commit('setESignatureError', RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE);
        } else {
          commit('setESignatureError', RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.GENERIC_ERROR);
        }
        console.error(error);
      }

      commit('setSaving', false);
    },

    async loadEventRunDetails({ commit }, { subject, event, repository, project, pipeline_version, data_version }) {
      if (!repository || !project || !event || !subject) {
        return;
      }

      let runDetails = null;

      commit('setRunsLoading', true);

      try {
        const evtResponse = await this._vm.$api('repositories.projects.pipelines.events.run.show', {
          version: '1.0',
          project,
          repository,
          event,
          subject,
          pipeline_version,
          data_version,
        });

        runDetails = evtResponse.data.data;

        commit('setRunDetails', runDetails);
      } catch (err) {
        console.error(err);
      } finally {
        commit('setRunsLoading', false);
      }
    },

    triggerOpenAddStage({ commit }) {
      commit('triggerOpenAddStage');
    },

    unloadEvent({ commit }) {
      commit('unloadEvent');
    },

    resetRunDetails({ commit }) {
      commit('setRunDetails', null);
    },

    async lockEventManually({ commit }, { repository, project, event, subject, reason }) {
      commit('setIsManualLockInProgress', true);
      try {
        const manualLockResponse = await this._vm.$api(
          'repositories.projects.subjects.events.lock',
          {
            repository,
            project,
            subject,
            event,
          },
          { reason }
        );

        if (![204, 200, 201].includes(manualLockResponse?.status)) {
          commit('setIsManualLockFail', true);

          return;
        }
        commit('setIsManualLockSuccessful', true);
      } catch (error) {
        console.error(error);
        commit('setIsManualLockFail', true);
      } finally {
        commit('setIsManualLockInProgress', false);
      }
    },

    async unlockEventManually({ commit }, { repository, project, event, subject, reason }) {
      commit('setIsManualUnLockInProgress', true);
      try {
        const manualUnlockResponse = await this._vm.$api(
          'repositories.projects.subjects.events.unlock',
          {
            repository,
            project,
            subject,
            event,
          },
          { reason }
        );

        if (![204, 200, 201].includes(manualUnlockResponse?.status)) {
          commit('setIsManualUnlockFail', true);

          return;
        }
        commit('setIsManualUnlockSuccessful', true);
      } catch (error) {
        console.error(error);
        commit('setIsManualUnlockFail', true);
      } finally {
        commit('setIsManualUnLockInProgress', false);
      }
    },

    resetOnManualLockEventFlag({ commit }) {
      commit('setIsManualLockSuccessful', false);
      commit('setIsManualLockFail', false);
      commit('setIsManualUnlockSuccessful', false);
      commit('setIsManualUnlockFail', false);
    },

    setSegmentationSaveStarted({ commit }, value) {
      commit('setSegmentationSaveStarted', value);
    },

    resetESignatureError({ commit }) {
      commit('setESignatureError', null);
    },
  },
};
